import { useCallback } from "react";
import { WorkDto, FinishWorkDTO, MatterWorkStatusDTO, TaskWorkStatus, Work, UnreleasedWork, ActiveWorkerDto, WorkSummaryForSheet } from "../types/work.types";
import { useAuthenticatedRequestService } from "./service.base";

export function useWorkService() {
  const WORK_PATH = "work";
  const { authGet, authPost, authPut } = useAuthenticatedRequestService();

  const getWorkStatus = useCallback(
    async function (): Promise<MatterWorkStatusDTO[]> {
      return await authGet(`${WORK_PATH}/status`);
    },
    [authGet]
  );

  const getTaskWorkStatus = useCallback(
    async function (matterId: string): Promise<TaskWorkStatus[]> {
      return await authGet(`${WORK_PATH}/${matterId}/taskstatus`);
    },
    [authGet]
  );

  const getWork = useCallback(
    async function (id: string): Promise<WorkDto> {
      return await authGet(`${WORK_PATH}/${id}`);
    },
    [authGet]
  );

  const getNextWork = useCallback(
    async function (): Promise<WorkDto | null> {
      return await authGet(`${WORK_PATH}/next`);
    },
    [authGet]
  );

  const getNextFlaggedWork = useCallback(
    async function (matterId: string): Promise<WorkDto | null> {
      return await authGet(`${WORK_PATH}/flagged/${matterId}`);
    },
    [authGet]
  );

  const getFlaggedWorkCount = useCallback(
    async function (matterId: string): Promise<number> {
      return await authGet(`${WORK_PATH}/flagged/${matterId}/count`);
    },
    [authGet]
  );

  const getSheetsWithUnreleasedWork = useCallback(
    async function (matterId: string): Promise<number[]> {
      return await authGet(`${WORK_PATH}/${matterId}/unreleased/sheets`);
    },
    [authGet]
  );
  const getUnavailableWorkCount = useCallback(
    async function (matterId: string, sheetNumber: string): Promise<number> {
      return await authGet(`${WORK_PATH}/unavailable/${matterId}/${sheetNumber}/count`);
    },
    [authGet]
  );

  const getUnavailableWork = useCallback(
    async function (matterId: string, sheetNumber: string): Promise<UnreleasedWork> {
      return await authGet(`${WORK_PATH}/unavailable/${matterId}/${sheetNumber}`);
    },
    [authGet]
  );

  const releaseUnavailableWork = useCallback(
    async function (matterId: string, sheetNumber: string, releaseWorkIds: number[]): Promise<UnreleasedWork> {
      return await authPost(`${WORK_PATH}/unavailable/${matterId}/${sheetNumber}/release`, releaseWorkIds);
    },
    [authPost]
  );

  const getPreviewRowWorkItems = useCallback(
    async function (matterId: string, sheetNumber: string): Promise<WorkDto[]> {
      return await authGet(`${WORK_PATH}/preview/${matterId}/${sheetNumber}/rows`);
    },
    [authGet]
  );

  const getPreviewFieldWorkItems = useCallback(
    async function (matterId: string, sheetNumber: string): Promise<WorkDto[]> {
      return await authGet(`${WORK_PATH}/preview/${matterId}/${sheetNumber}/fields`);
    },
    [authGet]
  );

  const getPreviewWorkItems = useCallback(
    async function (works: Work[]): Promise<WorkDto[]> {
      if (works.length === 0) return [];

      const workDtos: WorkDto[] = [];
      const matterId = works[0].matterId.toString();
      const sheetNumber = works[0].sheetNumber.toString();

      const hasRowWork = works.some((w) => w.rowNumber > 0);
      const hasFieldWork = works.some((w) => w.fieldNumber > 0);

      // Batch fetch all row work items
      if (hasRowWork) {
        const rowWorkDtos = await getPreviewRowWorkItems(matterId, sheetNumber);
        workDtos.push(...rowWorkDtos);
      }

      // Batch fetch all field work items (only once)
      if (hasFieldWork) {
        const fieldWorkDtos = await getPreviewFieldWorkItems(matterId, sheetNumber);
        workDtos.push(...fieldWorkDtos);
      }

      return workDtos;
    },
    [getPreviewRowWorkItems, getPreviewFieldWorkItems]
  );

  const getPreviousWork = useCallback(
    async function (workId: string): Promise<WorkDto | null> {
      return await authGet(`${WORK_PATH}/${workId}/previous`);
    },
    [authGet]
  );

  const updateWork = useCallback(
    async function (workId: string, work: FinishWorkDTO): Promise<null> {
      return await authPut(`${WORK_PATH}/${workId}`, work);
    },
    [authPut]
  );

  const updateWorkWithOverride = useCallback(
    async function (workId: string, work: FinishWorkDTO): Promise<null> {
      return await authPut(`${WORK_PATH}/${workId}`, { ...work, isValidationOverride: true });
    },
    [authPut]
  );

  const getActiveWorkers = useCallback(
    async function(matterId:number): Promise<ActiveWorkerDto[]> {
      return await authGet(`${WORK_PATH}/${matterId}/active-workers`);
    },
    [authGet]
  );

  const getCirculatorWorkBySheetId = useCallback(
    async function(matterId: number, sheetNumber: number): Promise<{ [key: string]: number }> {
      return await authGet(`${WORK_PATH}/${matterId}/sheet/${sheetNumber}/circulator-work`);
    },
    [authGet]
  );

  const unassignWorker = useCallback(
    async function(matterId: number, userId: number): Promise<{ unassignedCount: number }> {
      return await authPost(`${WORK_PATH}/${matterId}/active-workers/${userId}/unassign`, {});
    },
    [authPost]
  );

  const unassignAllWorkers = useCallback(
    async function(matterId: number): Promise<{ unassignedCount: number }> {
      return await authPost(`${WORK_PATH}/${matterId}/active-workers/unassign-all`, {});
    },
    [authPost]
  );

  const getActiveReviewers = useCallback(
    async function(matterId: number): Promise<ActiveWorkerDto[]> {
      return await authGet(`${WORK_PATH}/${matterId}/active-reviewers`);
    },
    [authGet]
  );

  const unassignReviewer = useCallback(
    async function(matterId: number, userId: number): Promise<{ unassignedCount: number }> {
      return await authPost(`${WORK_PATH}/${matterId}/active-reviewers/${userId}/unassign`, {});
    },
    [authPost]
  );

  const unassignAllReviewers = useCallback(
    async function(matterId: number): Promise<{ unassignedCount: number }> {
      return await authPost(`${WORK_PATH}/${matterId}/active-reviewers/unassign-all`, {});
    },
    [authPost]
  );

  const autoCompleteDates = useCallback(
    async function(matterId: string): Promise<void> {
      return await authPost(`${WORK_PATH}/${matterId}/auto-complete/dates`, {});
    },
    [authPost]
  );

  const getWorkSummaryForSheet = useCallback(
    async function (matterId: string, sheetNumber: number): Promise<WorkSummaryForSheet> {
      return await authGet(`${WORK_PATH}/${matterId}/${sheetNumber}/summary`);
    },
    [authGet]
  );

  const funcs = {
    getActiveWorkers,
    getActiveReviewers,
    getCirculatorWorkBySheetId,
    unassignWorker,
    unassignAllWorkers,
    unassignReviewer,
    unassignAllReviewers,
    getWorkStatus,
    getTaskWorkStatus,
    getWork,
    getNextWork,
    getNextFlaggedWork,
    getFlaggedWorkCount,
    getSheetsWithUnreleasedWork,
    getUnavailableWorkCount,
    getUnavailableWork,
    releaseUnavailableWork,
    getPreviousWork,
    getPreviewFieldWorkItems,
    getPreviewRowWorkItems,
    getPreviewWorkItems,
    updateWork,
    updateWorkWithOverride,
    autoCompleteDates,
    getWorkSummaryForSheet,
  };
  return funcs;
}
